// auth.js - Fixed version with proper Hub handling
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { signIn as amplifySignIn, signOut as amplifySignOut, getCurrentUser as amplifyGetCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { useEffect, useState } from 'react';

export let isAuthConfigured = false;

export function configureAmplify({ userPoolId, userPoolWebClientId, region, oauthDomain }) {
  if (!userPoolId || !userPoolWebClientId) {
    console.warn("Amplify Auth not configured - missing required environment variables");
    isAuthConfigured = false;
    return;
  }

  const authConfig = {
    Cognito: {
      userPoolId,
      userPoolClientId: userPoolWebClientId,
      signUpVerificationMethod: 'code',
      loginWith: oauthDomain ? {
        oauth: {
          domain: oauthDomain,
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn: [window.location.origin],
          redirectSignOut: [window.location.origin],
          responseType: 'code',
        }
      } : undefined
    }
  };

  Amplify.configure({
    Auth: authConfig
  });
  
  isAuthConfigured = true;
}

export async function signIn(username, password) {
  if (!isAuthConfigured) {
    throw new Error('Authentication is not configured');
  }
  
  try {
    const { isSignedIn, nextStep } = await amplifySignIn({ username, password });
    
    if (isSignedIn) {
      return await getCurrentUser();
    }
    
    // Handle MFA or other auth challenges if needed
    if (nextStep) {
      console.log('Additional auth step required:', nextStep);
    }
    
    return null;
  } catch (error) {
    console.error('Sign in error:', error);
    throw error;
  }
}

export async function signOut() {
  if (!isAuthConfigured) {
    throw new Error('Authentication is not configured');
  }
  
  try {
    await amplifySignOut();
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
}

export async function getCurrentUser() {
  if (!isAuthConfigured) {
    return null;
  }
  
  try {
    const user = await amplifyGetCurrentUser();
    const session = await fetchAuthSession();
    
    return {
      ...user,
      tokens: session.tokens
    };
  } catch (error) {
    // User is not authenticated
    return null;
  }
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let unsubscribe;

    const checkAuth = async () => {
      try {
        const currentUser = await getCurrentUser();
        if (mounted) {
          setUser(currentUser);
        }
      } catch (error) {
        if (mounted) {
          setUser(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    checkAuth();

    // Set up auth state listener - with proper error handling
    try {
      if (Hub && typeof Hub.listen === 'function') {
        unsubscribe = Hub.listen('auth', ({ payload }) => {
          const { event } = payload;
          if (event === 'signedIn' || event === 'signIn') {
            checkAuth();
          } else if (event === 'signedOut' || event === 'signOut') {
            if (mounted) {
              setUser(null);
            }
          }
        });
      }
    } catch (error) {
      console.warn('Hub listener setup failed:', error);
      // Continue without Hub - the app will still work
    }

    return () => {
      mounted = false;
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  return { user, loading };
}

// Password reset functions
export async function forgotPassword(username) {
  if (!isAuthConfigured) {
    throw new Error('Authentication is not configured');
  }
  
  try {
    const { resetPassword } = await import('aws-amplify/auth');
    return await resetPassword({ username });
  } catch (error) {
    console.error('Forgot password error:', error);
    throw error;
  }
}

export async function forgotPasswordSubmit(username, confirmationCode, newPassword) {
  if (!isAuthConfigured) {
    throw new Error('Authentication is not configured');
  }
  
  try {
    const { confirmResetPassword } = await import('aws-amplify/auth');
    return await confirmResetPassword({ username, confirmationCode, newPassword });
  } catch (error) {
    console.error('Confirm password reset error:', error);
    throw error;
  }
}